"use client";

import React, { useEffect, useRef, useState } from "react";
import RevealElement from "../revealElement";
import BottomCurves from "../curves/bottomCurves";

type CategoryShowcaseData = {
  eyebrow: string;
  heading: string;
  desc: string;
  categoriesLabel: string;
  categories: string[];
  waveBgColor?: { waveBgColor: string };
};

type CategoryShowcaseProps = {
  data?: Partial<CategoryShowcaseData>;
};

const DEFAULT_DATA: CategoryShowcaseData = {
  eyebrow: "What You Create",
  heading: "Every Category, One Network",
  desc: "From food to fintech, Kingdom Digital matches you with briefs that genuinely fit what you create.",
  categoriesLabel: "Content categories",
  categories: [
    "Food",
    "Beauty",
    "Parenting",
    "Family",
    "Lifestyle",
    "Travel",
    "Home",
    "Tech",
    "Fitness",
    "Fashion",
    "Gaming",
    "Automotive",
    "Pets",
    "Finance",
  ],
  waveBgColor: { waveBgColor: "dark purple" },
};

// Turquoise/green are darkened from the brand tokens (only for these chips,
// not the global Tailwind colors) so white text stays readable on them.
const CHIP_STYLES = [
  { bg: "bg-purple", text: "text-white" },
  { bg: "bg-[#0E7C76]", text: "text-white" },
  { bg: "bg-[#007A43]", text: "text-white" },
  { bg: "bg-blue", text: "text-white" },
];

// Explicit per-category style so categories 4 apart in the list (e.g.
// Parenting/Home/Gaming) don't collide when cycled by index.
const CATEGORY_STYLE_INDEX: Record<string, number> = {
  Food: 0,
  Beauty: 1,
  Parenting: 2,
  Family: 3,
  Lifestyle: 0,
  Travel: 1,
  Home: 3,
  Tech: 2,
  Fitness: 0,
  Fashion: 3,
  Gaming: 1,
  Automotive: 2,
  Pets: 0,
  Finance: 3,
};

const ROTATIONS = [-3, 2, -2, 3, -1, 1, -4, 2, -2, 4, -1, 3, -3, 1];

export default function CategoryShowcase({ data }: CategoryShowcaseProps) {
  const content: CategoryShowcaseData = { ...DEFAULT_DATA, ...data };
  const cloudRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = cloudRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-midGrey pt-12 pb-16 md:pb-24">
      <RevealElement className="px-7 sm:px-[10.417vw]">
        <p className="text-center text-purple font-mono uppercase tracking-[0.2em] body-small-fontsize mb-4">
          {content.eyebrow}
        </p>
        <h2 className="text-[#263244] text-center">{content.heading}</h2>
        <p className="text-center md:px-20">{content.desc}</p>
      </RevealElement>

      <div ref={cloudRef} className="mt-14 mb-16 md:mb-20 px-7 sm:px-[10.417vw]">
        <p className="font-mono uppercase tracking-[0.15em] text-xs text-darkGrey2 mb-7 text-center">
          {content.categoriesLabel}
        </p>
        <div className="flex flex-wrap justify-center gap-3.5 max-w-4xl mx-auto">
          {content.categories.map((category, i) => {
            const style =
              CHIP_STYLES[
                CATEGORY_STYLE_INDEX[category] ?? i % CHIP_STYLES.length
              ];
            return (
              <span
                key={category}
                className="category-chip-wrap inline-flex"
                style={{
                  animationDelay: `${-(i % 6) * 0.7}s`,
                  animationDuration: `${4 + (i % 4) * 0.6}s`,
                }}
              >
                <span
                  className={`category-chip inline-flex items-center font-bold uppercase tracking-wide text-sm rounded-full px-5 py-2.5 shadow-md cursor-default ${style.bg} ${style.text}`}
                  style={{
                    transform: `rotate(${ROTATIONS[i % ROTATIONS.length]}deg) ${
                      revealed ? "" : "translateY(14px) scale(0.85)"
                    }`,
                    opacity: revealed ? 1 : 0,
                    transitionDelay: `${i * 45}ms`,
                  }}
                >
                  {category}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .category-chip {
          transition:
            opacity 0.5s ease,
            transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .category-chip:hover {
          transform: rotate(0deg) scale(1.08) !important;
          transition-delay: 0s !important;
        }
        /* Continuous drift loop, mobile only. Runs on the wrapper so it
           doesn't fight the per-chip rotate/reveal transform on the inner
           span. Per-chip delay/duration are set inline for desync. */
        @media (max-width: 767px) {
          .category-chip-wrap {
            will-change: transform;
            animation-name: chipDrift;
            animation-timing-function: ease-in-out;
            animation-iteration-count: infinite;
            animation-direction: alternate;
          }
        }
        @keyframes chipDrift {
          from {
            transform: translateY(-4px) rotate(-1.5deg);
          }
          to {
            transform: translateY(4px) rotate(1.5deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .category-chip {
            transition: none;
          }
          .category-chip-wrap {
            animation: none !important;
          }
        }
      `}</style>

      <BottomCurves
        color={content.waveBgColor?.waveBgColor || "dark purple"}
        direction="right"
      />
    </section>
  );
}
